import { useContext } from 'react';
import { AppContext } from 'contexts/AppContext';
import { Button } from 'components/ui/button';
import { CalendarIcon } from 'lucide-react';

/**
 * Date range control for the tabs that aren't tables.
 *
 * WHY THIS EXISTS
 * ---------------
 * The only date picker in the console lives inside `DataTableToolbar`, which is
 * rendered only on the Messages and Users tabs. Sessions, Overview, Outcomes and
 * Quality read `dateRange` from context but had no way to change it — so every
 * one of them was pinned to the 14-day default with no visible control.
 *
 * WHY THESE NUMBERS
 * -----------------
 * 7 / 14 / 28 / 56, not 7 / 30 / 90. Every option is a whole number of weeks.
 *
 * Traffic here is strongly weekly — daily conversations range from 11 to 52,
 * with weekend troughs. A 30-day window contains a different number of weekends
 * than the 30 days before it, so part of any period-over-period delta is just
 * calendar alignment. Overview grades that (`delta_reliability`) and warns, and
 * with only a 30-day option available the warning could never be cleared: there
 * was no range a user could pick that qualified as sound.
 *
 * 28 and 56 grade `ok`. 7 and 14 still warn — correctly, because at this volume
 * a fortnight genuinely cannot carry a trend — but at least the honest option
 * now exists.
 */

const PRESETS = [
  { days: 7, label: '7 days' },
  { days: 14, label: '2 weeks' },
  { days: 28, label: '4 weeks' },
  { days: 56, label: '8 weeks' },
];

/**
 * Midnight UTC on the day `daysAgo` before the current UTC day.
 *
 * Every getter must be a UTC one. Reading local calendar parts and passing
 * them to `Date.UTC` makes "today" the LOCAL date while the data and the query
 * filtering it are UTC — and west of Greenwich those disagree for the last
 * hours of every day. At 20:26 EDT it is already 00:26 UTC tomorrow, so the
 * range ended at yesterday's UTC midnight and the backend's
 * `time_stamp < (end::date + 1 day)` silently dropped everything since.
 * No preset could reach it, because all four end here.
 *
 * Mirrors `utcMidnight` in contexts/AppContext.js — keep the two in step.
 */
function utcMidnight(daysAgo) {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 0, 0, 0, 0)
  );
}

const dayCount = (range) => {
  const [from, to] = Array.isArray(range) ? range : [];
  if (!from || !to) return null;
  return Math.round((new Date(to) - new Date(from)) / 86400000) + 1;
};

const fmt = (d) => (d ? new Date(d).toISOString().split('T')[0] : '—');

export default function DateRangePresets({ showPlatform = true, showLanguage = false }) {
  const {
    dateRange, setDateRange, platformFilter, setPlatformFilter,
    environmentFilter, setEnvironmentFilter,
    languageFilter, setLanguageFilter,
  } = useContext(AppContext);
  const active = dayCount(dateRange);
  const [from, to] = Array.isArray(dateRange) ? dateRange : [];

  const apply = (days) => {
    // days - 1 because the range is inclusive of both ends: "7 days" means
    // today and the six before it, not today and the seven before it.
    setDateRange([utcMidnight(days - 1), utcMidnight(0)]);
  };

  return (
    <div className="mb-5 space-y-2">
      {/* Row 1: the date range. Its own line so the filter groups below can
        * never interleave with it. */}
      <div className="flex flex-wrap items-center gap-2">
        <CalendarIcon className="h-4 w-4 text-gray-500" />
        {PRESETS.map((p) => (
          <Button
            key={p.days}
            size="sm"
            variant={active === p.days ? 'default' : 'outline'}
            onClick={() => apply(p.days)}
          >
            {p.label}
          </Button>
        ))}
        <input
          type="date"
          aria-label="Range start"
          value={fmt(from)}
          max={fmt(to)}
          onChange={(e) => e.target.value && setDateRange([new Date(`${e.target.value}T00:00:00Z`), to])}
          className="text-xs border border-zinc-300 dark:border-zinc-700 rounded px-2 py-1 bg-transparent"
        />
        <span className="text-xs text-gray-400">→</span>
        <input
          type="date"
          aria-label="Range end"
          value={fmt(to)}
          min={fmt(from)}
          onChange={(e) => e.target.value && setDateRange([from, new Date(`${e.target.value}T00:00:00Z`)])}
          className="text-xs border border-zinc-300 dark:border-zinc-700 rounded px-2 py-1 bg-transparent"
        />
        <span className="text-xs text-gray-500 ml-1">
          {fmt(from)} → {fmt(to)}
          {active ? ` · ${active} days` : ''}
          {active && active % 7 !== 0 && (
            <span className="text-amber-700 dark:text-amber-300">
              {' '}· not a whole number of weeks
            </span>
          )}
          {/* The range is applied in UTC days and every timestamp is shown in
              UTC (helperFunctions.formatUtcDateTime); say so where the range is. */}
          <span className="text-gray-400"> · all times UTC</span>
        </span>
      </div>

      {/* Row 2: the filter groups. The row may wrap, but each GROUP is a
        * single flex item, so a trio can never split mid-group -- the old
        * one-container layout broke exactly there: platform's "All" stayed on
        * one line beside the language buttons while Web/Mobile wrapped to the
        * next, reading as if "All" belonged to languages. Every group also
        * puts its "All …" option LAST and names its dimension, so a button
        * read in isolation still says what it is. */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {/* Environment. 'Prod' excludes rows labeled stage or local and keeps
          * unlabeled ones -- the tabs say how many. Unlike platform this is
          * shown on EVERY tab: all endpoints honour it. */}
        <div className="flex items-center gap-2">
          {[{ v: 'prod', l: 'Prod' }, { v: 'stage', l: 'Stage' }, { v: 'all', l: 'All env' }].map((o) => (
            <Button
              key={o.l}
              size="sm"
              variant={environmentFilter === o.v ? 'default' : 'outline'}
              onClick={() => setEnvironmentFilter(o.v)}
            >
              {o.l}
            </Button>
          ))}
        </div>

        {/* Language, from the stored chat_language column ('en'/'es' only,
          * measured). Shown ONLY on tabs whose every figure honors it --
          * Overview, Quality, Sessions -- set per tab in monitoring/Index.js,
          * same no-inert-buttons rule as platform. */}
        {showLanguage && (
          <div className="flex items-center gap-2 border-l border-zinc-300 dark:border-zinc-700 pl-4">
            {[{ v: 'en', l: 'EN' }, { v: 'es', l: 'ES' }, { v: null, l: 'All lang' }].map((o) => (
              <Button
                key={o.l}
                size="sm"
                variant={languageFilter === o.v ? 'default' : 'outline'}
                onClick={() => setLanguageFilter(o.v)}
              >
                {o.l}
              </Button>
            ))}
          </div>
        )}

        {/* Platform, derived from messages.device server-side. Hidden on tabs
          * that do not read the global filter -- Sessions has its own control,
          * and buttons that silently do nothing are worse than none.
          * `showPlatform` is set per tab in monitoring/Index.js. */}
        {showPlatform && (
          <div className="flex items-center gap-2 border-l border-zinc-300 dark:border-zinc-700 pl-4">
            {[{ v: 'web', l: 'Web' }, { v: 'mobile', l: 'Mobile' }, { v: null, l: 'All platforms' }].map((o) => (
              <Button
                key={o.l}
                size="sm"
                variant={platformFilter === o.v ? 'default' : 'outline'}
                onClick={() => setPlatformFilter(o.v)}
              >
                {o.l}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
